import React, { useEffect, useState } from 'react';
import {
    DataGridPro,
    GridColDef,
    GridRenderCellParams,
    GridRowId,
    GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
/* ICONS */
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CachedIcon from '@mui/icons-material/Cached';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ApiIcon from '@mui/icons-material/Api';
/* COMPONENTS */
import { ListItemIcon, ListItemText, Menu, MenuItem, Snackbar, Alert, Divider } from '@mui/material';
import type { AlertColor } from '@mui/material';
/* ACTIONS */
import { AddReports } from 'components/dialog/reports/AddReports';
import { EditReports } from 'components/dialog/reports/EditReports';
import { DeactivateReports } from 'components/dialog/reports/DeactivateReports';
/* REQUEST */
import { getReports } from 'utils/Reports';
import { Edit as EditApiUsed } from 'components/dialog/reports/apis/Edit';
import { ContextProvider } from 'components/list/reports/Context';

interface ReportRow {
    id: GridRowId;
    name: string;
    status: string;
    for_employees: string;
    slug: string;
    category: string | number;
    create_by: string;
    update_at: string;
    update_by: string;
    description: string;
    json_apis: string;
    template: string;
    properties: unknown;
}

interface ContextMenuState {
    mouseX: number;
    mouseY: number;
}

interface AlertState {
    msg: string;
    severity: AlertColor;
}

interface ContextMenuProps {
    contextMenu: ContextMenuState | null;
    setContextMenu: React.Dispatch<React.SetStateAction<ContextMenuState | null>>;
    selectedRow: GridRowSelectionModel;
    reloading: () => Promise<void>;
    rows: ReportRow[];
}

interface ContextOption {
    name: string;
    icon: React.ReactNode;
    click: () => void;
    color: string;
    selected: boolean;
    onlyOne: boolean;
    disabled?: boolean;
    divider?: boolean;
}

const ContextMenu = ({ contextMenu, setContextMenu, selectedRow, reloading, rows }: ContextMenuProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openEditApi, setOpenEditApi] = useState(false);
    const [openDeactivate, setOpenDeactivate] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [alert, setAlert] = useState<AlertState>({ msg: '', severity: 'info' });

    const permissions = JSON.parse(localStorage.getItem('permissions') ?? '[]') as string[];
    const hasPermission = (permission: string) => permissions.includes(permission);

    const select = rows.find((row) => row.id === selectedRow[0]);

    const options: ContextOption[] = [
        {
            name: t('custom_reports.reports.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                setOpenAdd(true);
                setContextMenu(null);
            },
            color: '',
            selected: false,
            onlyOne: false,
            disabled: !hasPermission('custom_reports.CreateReports'),
        },
        {
            name: t('custom_reports.reports.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                setOpenEdit(true);
                setContextMenu(null);
            },
            color: '',
            selected: true,
            onlyOne: true,
            disabled: !hasPermission('custom_reports.UpdateReports'),
        },
        {
            name: t('custom_reports.reports.table.context_menu.edit_api'),
            icon: <WorkspacesIcon />,
            click: () => {
                setOpenEditApi(true);
                setContextMenu(null);
            },
            color: '',
            selected: true,
            onlyOne: true,
            disabled: selectedRow.length !== 1,
        },
        {
            name: t('custom_reports.reports.table.context_menu.edit_api'),
            icon: <WorkspacesIcon />,
            click: () => {
                setOpenEditApi(true);
                setContextMenu(null);
            },
            color: '',
            selected: true,
            onlyOne: true,
            disabled: selectedRow.length !== 1,
        },
        {
            name:
                select?.status === 'Enabled'
                    ? t('custom_reports.reports.table.context_menu.deactivate')
                    : t('custom_reports.reports.table.context_menu.activate'),
            icon: <DeleteIcon />,
            click: () => {
                setOpenDeactivate(true);
                setContextMenu(null);
            },
            color: '',
            selected: true,
            onlyOne: true,
            disabled: !hasPermission('custom_reports.DeactivateReports'),
        },
        {
            name:
                select?.template === null || select?.template === ''
                    ? t('custom_reports.reports.table.context_menu.add_template')
                    : t('custom_reports.reports.table.context_menu.edit_template'),
            icon: <IntegrationInstructionsIcon />,
            click: () => {
                const selectedReportId = selectedRow[0];
                navigate(`/custom_reports?subpage=template&report_id=${selectedReportId}`);
            },
            color: '',
            selected: true,
            onlyOne: true,
            disabled: !hasPermission('custom_reports.UpdateReports'),
        },
        {
            name:
                select?.json_apis === null || select?.json_apis === ''
                    ? t('custom_reports.reports.table.context_menu.add_api_used')
                    : t('custom_reports.reports.table.context_menu.edit_api_used'),
            icon: <ApiIcon />,
            click: () => console.info('Api used:', select?.json_apis),
            color: '',
            selected: true,
            onlyOne: true,
            disabled: !hasPermission('custom_reports.UpdateReports'),
        },
        {
            name: t('custom_reports.reports.table.context_menu.reload'),
            icon: <CachedIcon />,
            click: () => {
                void reloading();
                setContextMenu(null);
            },
            color: '',
            selected: false,
            onlyOne: false,
        },
    ];

    return (
        <>
            <Menu
                open={Boolean(contextMenu)}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options.map((option, index) => (
                    <div key={index} hidden={option.onlyOne && selectedRow.length !== 1}>
                        {option.divider ? <Divider /> : null}

                        <MenuItem onClick={option.click} disabled={option.disabled}>
                            <ListItemIcon>{option.icon}</ListItemIcon>
                            <ListItemText
                                primary={option.name}
                                primaryTypographyProps={{ color: option.color || undefined }}
                            />
                        </MenuItem>
                    </div>
                ))}
            </Menu>
            <AddReports
                open={openAdd}
                setOpen={setOpenAdd}
                rows={rows}
                setAlert={setAlert}
                setOpenAlert={setOpenAlert}
                reloading={reloading}
            />
            <EditReports
                open={openEdit}
                setOpen={setOpenEdit}
                selected={selectedRow}
                rows={rows}
                setAlert={setAlert}
                setOpenAlert={setOpenAlert}
                reloading={reloading}
            />
            <ContextProvider>
                <EditApiUsed open={openEditApi} setOpen={setOpenEditApi} selectedRow={selectedRow} rows={rows} />
            </ContextProvider>
            <DeactivateReports
                open={openDeactivate}
                setOpen={setOpenDeactivate}
                selected={selectedRow}
                rows={rows}
                setAlert={setAlert}
                setOpenAlert={setOpenAlert}
                reloading={reloading}
            />
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={openAlert}
                onClose={() => setOpenAlert(false)}
                autoHideDuration={6000}
                key={'top,right'}
                sx={{ width: '100%' }}
            >
                <Alert severity={alert.severity} onClose={() => setOpenAlert(false)} variant="outlined">
                    {alert.msg}
                </Alert>
            </Snackbar>
        </>
    );
};

const ReportsTable = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<ReportRow[]>([]);
    const [selectedRow, setSelectedRow] = useState<GridRowSelectionModel>([]);
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
    const [loading, setLoading] = useState(true);

    const columns: GridColDef<ReportRow>[] = [
        {
            field: 'name',
            headerName: t('custom_reports.reports.table.columns.name'),
            flex: 1,
            filterable: false,
        },
        {
            field: 'for_employees',
            headerName: t('custom_reports.reports.table.columns.for_employees'),
            flex: 1,
            filterable: false,
        },
        {
            field: 'slug',
            headerName: t('custom_reports.reports.table.columns.slug'),
            flex: 1,
            filterable: false,
        },
        {
            field: 'category',
            headerName: t('custom_reports.reports.table.columns.category'),
            flex: 1,
            filterable: false,
        },
        {
            field: 'create_by',
            headerName: t('custom_reports.reports.table.columns.create_by'),
            flex: 1,
            filterable: false,
        },
        {
            field: 'update_at',
            headerName: t('custom_reports.reports.table.columns.update_at'),
            flex: 1,
            filterable: false,
        },
        {
            field: 'update_by',
            headerName: t('custom_reports.reports.table.columns.update_by'),
            flex: 1,
            filterable: false,
        },
        {
            field: 'status',
            headerName: t('custom_reports.reports.table.columns.status'),
            flex: 1,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<ReportRow, string>) => {
                return params.value === 'Enabled' ? <CheckIcon color="success" /> : <CloseIcon color="error" />;
            },
        },
    ];

    const reloading = async (): Promise<void> => {
        setLoading(true);
        try {
            const response = (await getReports()) as Array<Record<string, unknown>>;
            const records: ReportRow[] = response.map((record) => {
                return {
                    id: record.id as GridRowId,
                    name: (record.name as string) ?? '',
                    status: (record.status as string) ?? '',
                    for_employees: (record.for_employees as string) ?? '',
                    slug: (record.slug as string) ?? '',
                    category: (record.category_id as string | number) ?? '',
                    create_by: (record.created_by as string) ?? '',
                    update_at: (record.updated_at as string) ?? '',
                    update_by: (record.updated_by as string) ?? '',
                    description: (record.description as string) ?? '',
                    json_apis: (record.json_apis as string) ?? '',
                    template: (record.template as string) ?? '',
                    properties: record.properties,
                };
            });
            setRows(records);
            setLoading(false);
        } catch (error: unknown) {
            if (error instanceof Error) {
                console.error(error.message);
            }
            setRows([]);
            setLoading(false);
        }
        setLoading(false);
    };

    useEffect(() => {
        void reloading();
    }, []);

    return (
        <>
            <div
                style={{ width: '100%', height: '100%' }}
                onContextMenu={(event) => {
                    event.preventDefault();
                    setContextMenu(
                        contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null,
                    );
                }}
            >
                <DataGridPro
                    sx={{ height: 'calc(100vh - 140px)' }}
                    columns={columns}
                    rows={rows}
                    showCellVerticalBorder
                    showColumnVerticalBorder
                    headerFilters
                    localeText={{
                        headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                        headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                        headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                        headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                        headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                        headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                        footerRowSelected: (count) =>
                            count > 1
                                ? t('general.table.footerFilters.footerRowSelected.>1', {
                                      count: count.toLocaleString(),
                                  })
                                : t('general.table.footerFilters.footerRowSelected.=1', {
                                      count: count.toLocaleString(),
                                  }),

                        footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                        MuiTablePagination: {
                            labelDisplayedRows: ({ from, to, count }) =>
                                t('general.table.footerFilters.labelDisplayedRows', {
                                    from: from.toLocaleString(),
                                    to: to.toLocaleString(),
                                    count: count.toLocaleString(),
                                }),
                            labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                        },
                    }}
                    disableColumnMenu
                    loading={loading}
                    checkboxSelection
                    onRowSelectionModelChange={(newRowSelectionModel: GridRowSelectionModel) => {
                        setSelectedRow(newRowSelectionModel);
                    }}
                    rowSelectionModel={selectedRow}
                    pagination
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                category: false,
                                create_by: false,
                                update_by: false,
                            },
                        },
                    }}
                />

                <ContextMenu
                    contextMenu={contextMenu}
                    setContextMenu={setContextMenu}
                    selectedRow={selectedRow}
                    reloading={reloading}
                    rows={rows}
                />
            </div>
        </>
    );
};

export { ReportsTable };